import { groupBy } from 'lodash'
import { useEffect, useMemo, useState } from 'react'

import { Dayjs, ISO_DATE_FORMAT, dayjsEn } from '@bemlo/date'
import { Country } from '@bemlo/enums'

import { useTranslations } from '../../../../translate'

type Holiday = {
  date: Dayjs
  name: string
  isPublic: boolean
}

/**
 * Represents a scheduled day with various date formats and properties.
 */
export type ScheduleDay = {
  /**
   * The date object for the day.
   */
  date: Dayjs

  /**
   * The ISO formatted date string (YYYY-MM-DD).
   */
  dateISO: string

  /**
   * A long formatted date string (e.g., ddd D/M).
   */
  dateLong: string

  /**
   * A short formatted date string (e.g., D/M).
   */
  dateShort: string

  /**
   * The week string formatted as YYYY-WW.
   */
  week: string

  /**
   * The ISO week number of the year.
   */
  weekNumber: number

  /**
   * Indicates if the day is the current day.
   */
  isToday: boolean

  /**
   * Indicates if the day is a weekend day (Saturday or Sunday)
   */
  isWeekend: boolean

  /**
   * Indicates if the day is a bank holiday (Sundays only for MVP).
   */
  holiday: Holiday | null

  /**
   * Indicates if the day is within the range of padded weeks or months.
   * This can occur when padding out the schedule to include days outside of the selected range.
   */
  withinRange: boolean
}

type Week = {
  key: string
  number: number
  days: ScheduleDay[]
}

/**
 * Prevents an infinite loop if the input is bad (should not happen).
 */
const MAX_DAYS = 366

export const useDaysInRange = (
  startsAt: number,
  endsAt: number,
  country: Country | undefined,
  roundTo: 'week' | 'month' | null = 'week',
): [ScheduleDay[], Week[]] => {
  const t = useTranslations()
  const [holidays, setHolidays] = useState<Record<string, Holiday>>({})

  useEffect(() => {
    let isMounted = true // To prevent state updates after unmount

    const loadHolidays = async () => {
      if (country) {
        // Dynamically import the getHolidays function
        const { getHolidays } = await import('@bemlo/holidays')
        const holidaysData = await getHolidays(
          country,
          t.$date.unix(startsAt),
          t.$date.unix(endsAt),
        )

        if (isMounted) {
          setHolidays(
            holidaysData.reduce(
              (acc, holiday) => {
                acc[holiday.date.format(ISO_DATE_FORMAT)] = holiday
                return acc
              },
              {} as Record<string, Holiday>,
            ),
          )
        }
      } else {
        if (isMounted) {
          setHolidays({})
        }
      }
    }

    void loadHolidays()

    return () => {
      isMounted = false
    }
  }, [country, startsAt, endsAt])

  return useMemo(() => {
    const now = dayjsEn()
    const startDate = t.$date.unix(startsAt)
    const endDate = t.$date.unix(endsAt)
    const startDateRounded = roundTo ? startDate.startOf(roundTo) : startDate
    const endDateRounded = roundTo ? endDate.endOf(roundTo) : endDate

    const days: ScheduleDay[] = []

    let currentDate = startDateRounded
    let i = 0

    while (
      (currentDate.isSame(endDateRounded) ||
        currentDate.isBefore(endDateRounded)) &&
      i < MAX_DAYS
    ) {
      const date = currentDate

      const dateISO = date.format(ISO_DATE_FORMAT)
      const dateLong = date.format('ddd **D/M**')
      const dateShort = date.format('**D/M**')
      const week = date.format('GGGG-WW')
      const weekNumber = date.isoWeek()
      const isToday = date.isSame(now, 'day')
      const holiday = holidays[dateISO] ?? null
      const isWeekend = date.isoWeekday() >= 6
      const withinRange = !date.isBefore(startDate) && !date.isAfter(endDate)

      days.push({
        date,
        dateISO,
        dateLong,
        dateShort,
        week,
        weekNumber,
        isToday,
        holiday,
        isWeekend,
        withinRange,
      })

      currentDate = currentDate.add(1, 'day')
      i++
    }

    // Group days by week and sort the weeks
    const weeks = Object.entries(groupBy(days, (day) => day.week))
      .sort(([weekA], [weekB]) => weekA.localeCompare(weekB))
      .map(
        ([_, daysInWeek]): Week => ({
          days: daysInWeek,
          key: daysInWeek[0].week,
          number: daysInWeek[0].weekNumber,
        }),
      )

    return [days, weeks]
  }, [startsAt, endsAt, t, holidays])
}
